import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Download, Loader2, AlertTriangle, CheckCircle2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/axios';
import { getErrorMessage } from '@/lib/errors';

interface Exportacion {
  id: string;
  estado: 'EN_COLA' | 'PROCESANDO' | 'LISTA' | 'FALLIDA';
  totalPacientes: number;
  pacientesProcesados: number;
  archivoResultadoUrlFirmada: string | null;
  archivoResultadoExpiraEn: string | null;
  fechaSolicitud: string;
  fechaCompletado: string | null;
  errorDetalle: string | null;
}

function ProgressBar({ procesados, total }: { procesados: number; total: number }) {
  const pct = total > 0 ? Math.round((procesados / total) * 100) : 0;
  return (
    <div className="w-full bg-gray-100 rounded-full h-2 mt-2">
      <div
        className="bg-primary-500 h-2 rounded-full transition-all duration-500"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

export function ExportacionMasivaPanel() {
  const qc = useQueryClient();
  const [confirming, setConfirming] = useState(false);

  const { data: ultima, isLoading } = useQuery<Exportacion | null>({
    queryKey: ['exportacion-masiva-ultima'],
    queryFn: () => api.get('/api/exportacion-masiva/ultima').then(r => r.data),
    refetchInterval: (query) => {
      const estado = query.state.data?.estado;
      return estado === 'EN_COLA' || estado === 'PROCESANDO' ? 5000 : false;
    },
  });

  const solicitar = useMutation({
    mutationFn: () => api.post('/api/exportacion-masiva').then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['exportacion-masiva-ultima'] });
      setConfirming(false);
      toast.success('Exportación iniciada. Te notificaremos por email cuando esté lista.');
    },
    onError: (err: any) => {
      const code = err?.response?.data?.code ?? err?.response?.data?.message?.code;
      if (code === 'EXPORTACION_EN_PROGRESO') {
        toast.info('Ya hay una exportación en proceso. Espera a que finalice.');
      } else {
        toast.error(getErrorMessage(err, 'Error al iniciar la exportación.'));
      }
      setConfirming(false);
    },
  });

  const refrescar = useMutation({
    mutationFn: (id: string) => api.post(`/api/exportacion-masiva/${id}/refrescar-url`).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['exportacion-masiva-ultima'] });
      toast.success('Enlace renovado por 48 horas más.');
    },
    onError: (err: any) => toast.error(getErrorMessage(err, 'No se pudo renovar el enlace.')),
  });

  const enProgreso = ultima?.estado === 'EN_COLA' || ultima?.estado === 'PROCESANDO';
  const expirado = ultima?.archivoResultadoExpiraEn
    ? new Date(ultima.archivoResultadoExpiraEn) < new Date()
    : false;

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-text-secondary p-4">
        <Loader2 size={15} className="animate-spin" />
        Cargando estado de exportación...
      </div>
    );
  }

  return (
    <div className="space-y-4">

      {/* Última exportación */}
      {ultima && (
        <div className="rounded-lg border border-gray-100 bg-gray-50 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-text-primary">Última exportación</p>
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
              ultima.estado === 'LISTA'      ? 'bg-green-100 text-green-700' :
              ultima.estado === 'FALLIDA'    ? 'bg-red-100 text-red-600' :
              ultima.estado === 'PROCESANDO' ? 'bg-blue-100 text-blue-700' :
              'bg-yellow-100 text-yellow-700'
            }`}>
              {ultima.estado === 'EN_COLA'    ? 'En cola' :
               ultima.estado === 'PROCESANDO' ? 'Procesando' :
               ultima.estado === 'LISTA'      ? 'Lista' : 'Fallida'}
            </span>
          </div>

          <p className="text-xs text-text-secondary">
            Solicitada el {new Date(ultima.fechaSolicitud).toLocaleString('es-MX')}
            {ultima.fechaCompletado && ` · Completada ${new Date(ultima.fechaCompletado).toLocaleString('es-MX')}`}
          </p>

          {/* Progreso */}
          {enProgreso && (
            <div>
              <p className="text-xs text-text-secondary">
                {ultima.pacientesProcesados} / {ultima.totalPacientes} pacientes procesados
              </p>
              <ProgressBar
                procesados={ultima.pacientesProcesados}
                total={ultima.totalPacientes}
              />
            </div>
          )}

          {/* Error */}
          {ultima.estado === 'FALLIDA' && ultima.errorDetalle && (
            <div className="flex items-start gap-2 text-xs text-red-600 bg-red-50 rounded p-2">
              <AlertTriangle size={13} className="flex-shrink-0 mt-0.5" />
              <span>Error: {ultima.errorDetalle.split('\n')[0]}</span>
            </div>
          )}

          {/* Descarga */}
          {ultima.estado === 'LISTA' && (
            <div className="flex items-center gap-2">
              {!expirado && ultima.archivoResultadoUrlFirmada ? (
                <a
                  href={ultima.archivoResultadoUrlFirmada}
                  download
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded-lg hover:bg-green-700 transition-colors"
                >
                  <Download size={13} />
                  Descargar ZIP
                </a>
              ) : (
                <p className="text-xs text-orange-600 font-medium">El enlace ha expirado</p>
              )}
              <button
                onClick={() => refrescar.mutate(ultima.id)}
                disabled={refrescar.isPending}
                className="flex items-center gap-1 text-xs text-text-secondary hover:text-text-primary transition-colors"
              >
                <RefreshCw size={12} className={refrescar.isPending ? 'animate-spin' : ''} />
                Renovar enlace (48h)
              </button>
            </div>
          )}
        </div>
      )}

      {/* Botón solicitar */}
      {!confirming ? (
        <button
          disabled={enProgreso}
          onClick={() => setConfirming(true)}
          className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white text-sm font-medium rounded-lg hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {enProgreso
            ? <><Loader2 size={14} className="animate-spin" /> Exportación en proceso...</>
            : <><Download size={14} /> Exportar todos los expedientes</>
          }
        </button>
      ) : (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 space-y-3">
          <div className="flex items-start gap-2">
            <AlertTriangle size={15} className="text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-amber-800">¿Confirmas la exportación masiva?</p>
              <p className="text-xs text-amber-700 mt-1">
                Se generará un archivo ZIP con los expedientes de todos los pacientes de la clínica.
                Recibirás un correo cuando esté listo para descargar. El proceso puede tardar varios minutos.
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => solicitar.mutate()}
              disabled={solicitar.isPending}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 text-white text-xs font-medium rounded-lg hover:bg-amber-700 disabled:opacity-60 transition-colors"
            >
              {solicitar.isPending ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
              Sí, iniciar exportación
            </button>
            <button
              onClick={() => setConfirming(false)}
              className="px-3 py-1.5 text-xs text-text-secondary hover:text-text-primary transition-colors"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      <p className="text-xs text-text-secondary">
        Solo puedes tener una exportación activa a la vez. El archivo ZIP estará disponible por 48 horas desde que se genera.
        Cumplimiento: NOM-004-SSA3-2012 · LFPDPPP.
      </p>
    </div>
  );
}
